import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  X, 
  Bug, 
  AlertTriangle, 
  Info, 
  CheckCircle, 
  Loader,
  Monitor,
  Globe,
  MessageSquare,
  Target,
  Eye,
  Zap
} from 'lucide-react';

const BugReportModal = ({ isOpen, onClose }) => {
  const { user, session } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    severity: 'medium',
    category: 'other',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Title and description are required');
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch('/api/bugs', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          pageUrl: window.location.href
        })
      });

      if (response.ok) {
        setSubmitted(true);
        // Reset form after a delay
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            title: '',
            description: '',
            severity: 'medium',
            category: 'other',
            stepsToReproduce: '',
            expectedBehavior: '',
            actualBehavior: ''
          });
          onClose();
        }, 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to submit bug report');
      }
    } catch (error) {
      console.error('Error submitting bug report:', error);
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const severityOptions = [
    { value: 'low', label: 'Low', icon: Info, color: 'text-blue-600', bg: 'bg-blue-50' },
    { value: 'medium', label: 'Medium', icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50' },
    { value: 'high', label: 'High', icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50' },
    { value: 'critical', label: 'Critical', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' }
  ];

  const categoryOptions = [
    { value: 'ui_bug', label: 'UI/Visual Bug', icon: Monitor },
    { value: 'functionality', label: 'Functionality Issue', icon: Zap },
    { value: 'performance', label: 'Performance Issue', icon: Target },
    { value: 'data_loss', label: 'Data Loss', icon: AlertTriangle },
    { value: 'security', label: 'Security Issue', icon: AlertTriangle },
    { value: 'integration', label: 'Integration Issue', icon: Globe },
    { value: 'other', label: 'Other', icon: MessageSquare }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl my-8 flex flex-col max-h-[calc(100vh-4rem)]">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6 relative rounded-t-2xl flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-red-100 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
          
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Bug className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Report a Bug</h2>
              <p className="text-red-100 mt-1">Help us improve Banyan by reporting issues</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Bug Report Submitted! 🐛
              </h3>
              <p className="text-gray-600 mb-4">
                Thank you for helping us improve Banyan. We've received your bug report and will investigate it shortly.
              </p>
              <p className="text-sm text-gray-500">
                You can track the status of your reports in your dashboard.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bug Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                  placeholder="Brief description of the bug (e.g., 'Dashboard fails to load')"
                />
              </div>

              {/* Severity and Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Severity *
                  </label>
                  <div className="space-y-2">
                    {severityOptions.map((option) => {
                      const Icon = option.icon;
                      return (
                        <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="radio"
                            name="severity"
                            value={option.value}
                            checked={formData.severity === option.value}
                            onChange={handleInputChange}
                            className="w-4 h-4 text-red-600 focus:ring-red-500"
                          />
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${option.bg}`}>
                            <Icon className={`h-4 w-4 ${option.color}`} />
                          </div>
                          <span className="text-sm font-medium text-gray-700">{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                  >
                    {categoryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MessageSquare className="h-4 w-4 inline mr-2" />
                  Detailed Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors resize-none"
                  placeholder="Describe the bug in detail. What exactly happened? When did it occur?"
                />
              </div>

              {/* Steps to Reproduce */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Target className="h-4 w-4 inline mr-2" />
                  Steps to Reproduce
                </label>
                <textarea
                  name="stepsToReproduce"
                  value={formData.stepsToReproduce}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors resize-none"
                  placeholder="1. Go to...&#10;2. Click on...&#10;3. Enter...&#10;4. Bug occurs..."
                />
              </div>

              {/* Expected vs Actual Behavior */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Eye className="h-4 w-4 inline mr-2" />
                    Expected Behavior
                  </label>
                  <textarea
                    name="expectedBehavior"
                    value={formData.expectedBehavior}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors resize-none"
                    placeholder="What should have happened?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Bug className="h-4 w-4 inline mr-2" />
                    Actual Behavior
                  </label>
                  <textarea
                    name="actualBehavior"
                    value={formData.actualBehavior}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors resize-none"
                    placeholder="What actually happened?"
                  />
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Automatically Collected Information
                </h4>
                <div className="text-xs text-gray-500 space-y-1">
                  <p>• Current page: {window.location.pathname}</p>
                  <p>• Browser: {navigator.userAgent.split(' ').slice(-2).join(' ')}</p>
                  <p>• Screen size: {window.screen.width}x{window.screen.height}</p>
                  <p>• Timestamp: {new Date().toLocaleString()}</p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-medium rounded-xl shadow-soft hover:shadow-medium transition-all duration-300 flex items-center space-x-2"
                >
                  {submitting ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Bug className="h-4 w-4" />
                      <span>Submit Bug Report</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default BugReportModal; 